import React, { useState } from 'react';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DoneIcon from '@mui/icons-material/Done';
import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';

type Props = {
  open: boolean;
  onClose: () => void;
  title: string;
  sql: string;
  executed?: boolean;
  parameters?: Record<string, unknown>;
  /** Named when the card reads a shared dataset rather than its own query. */
  datasetName?: string;
};

/**
 * The query behind a card, readable and copyable.
 *
 * A number nobody can trace is a number nobody trusts, and until now the only
 * way to see a card's SQL was to open the editor, which a viewer without edit
 * rights cannot do.
 */
export default function LensSqlDialog({ open, onClose, title, sql, datasetName, executed = false, parameters }: Props) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(sql);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      // A denied clipboard permission is not worth an error dialog: the SQL is
      // on screen and selectable, which is the fallback anyway.
      setCopied(false);
    }
  };

  return (
    <Dialog fullWidth maxWidth="md" onClose={onClose} open={open}>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack alignItems="center" direction="row" spacing={1}>
          <Typography component="span" sx={{ flex: 1 }} variant="h6">
            {title}
          </Typography>
          {datasetName && <Chip label={`Shared dataset: ${datasetName}`} size="small" variant="outlined" />}
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Typography color="text.secondary" sx={{ display: 'block', mb: 1 }} variant="caption">
          {executed
            ? 'This query produced the returned rows. Chart grouping and result filters may further shape what you see.'
            : 'Dataset source SQL. The executed query is not available for this result. Lens may apply additional query filters and chart grouping.'}
        </Typography>
        {parameters && Object.keys(parameters).length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2">Query parameters</Typography>
            {Object.entries(parameters).map(([name, value]) => (
              <Typography key={name} variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                @{name}: {typeof value === 'string' ? value : JSON.stringify(value)}
              </Typography>
            ))}
          </Box>
        )}
        <Box
          component="pre"
          sx={{
            backgroundColor: 'action.hover',
            borderRadius: 1,
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            fontSize: 12,
            lineHeight: 1.6,
            m: 0,
            maxHeight: '60vh',
            overflow: 'auto',
            p: 1.5,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
          tabIndex={0}
        >
          {sql}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button onClick={copy} startIcon={copied ? <DoneIcon /> : <ContentCopyIcon />} variant="contained">
          {copied ? 'Copied' : 'Copy SQL'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
